import {
	FC,
	useEffect,
	useState,
	useCallback
} from 'react'
import { useTranslation } from 'react-i18next'
import {
	Flex,
	Spin,
	Tag,
	Button,
	Modal,
	Form,
	Input,
	Select,
	DatePicker,
	Collapse,
	Timeline,
	Popconfirm,
	message,
	Typography,
	Divider,
	Empty
} from 'antd'
import {
	PlusOutlined,
	DeleteOutlined,
	ToolOutlined,
	CheckCircleOutlined,
	WarningOutlined,
	CloseCircleOutlined
} from '@ant-design/icons'
import { Equipment, EquipmentCondition, ServiceType } from '../helpers/types/inventory'
import {
	fetchEquipment,
	fetchEquipmentById,
	createEquipment,
	deleteEquipment,
	createEquipmentService
} from '../api/inventory'

const { Text, Title } = Typography

// ─── Condition badge ─────────────────────────────────────────────────────────
const conditionConfig: Record<
	EquipmentCondition,
	{ color: string; icon: React.ReactNode; label: string }
> = {
	good: {
		color: 'success',
		icon: <CheckCircleOutlined />,
		label: 'Good'
	},
	worn: {
		color: 'warning',
		icon: <WarningOutlined />,
		label: 'Worn'
	},
	damaged: {
		color: 'error',
		icon: <CloseCircleOutlined />,
		label: 'Damaged'
	}
}

const ConditionBadge: FC<{ condition: EquipmentCondition }> = ({
	condition
}) => {
	const cfg = conditionConfig[condition] ?? conditionConfig.good
	return (
		<Tag
			icon={cfg.icon}
			color={cfg.color}
			style={{ marginInlineEnd: 0 }}>
			{cfg.label}
		</Tag>
	)
}

// ─── Service record row ───────────────────────────────────────────────────────
const SERVICE_TYPE_LABELS: Record<ServiceType, string> = {
	replacement: 'Replacement',
	repair: 'Repair',
	calibration: 'Calibration',
	inspection: 'Inspection'
}

// ─── Add Equipment modal ──────────────────────────────────────────────────────
interface AddEquipmentModalProps {
	carrierId: number
	open: boolean
	onClose: () => void
	onCreated: (eq: Equipment) => void
}

const AddEquipmentModal: FC<AddEquipmentModalProps> = ({
	carrierId,
	open,
	onClose,
	onCreated
}) => {
	const { t } = useTranslation()
	const [form] = Form.useForm()
	const [saving, setSaving] = useState(false)

	const handleOk = async () => {
		const values = await form.validateFields()
		setSaving(true)
		try {
			const eq = await createEquipment({
				carrierId,
				...values,
				installedAt: values.installedAt
					? values.installedAt.toISOString()
					: undefined
			})
			onCreated(eq)
			form.resetFields()
			onClose()
		} catch {
			message.error(t('Error'))
		} finally {
			setSaving(false)
		}
	}

	return (
		<Modal
			title="Add Equipment"
			open={open}
			onCancel={onClose}
			onOk={handleOk}
			confirmLoading={saving}
			okText="Add"
			destroyOnClose>
			<Form form={form} layout="vertical">
				<Form.Item
					name="componentType"
					label="Component Type"
					rules={[{ required: true }]}>
					<Input placeholder="e.g. control_unit, contactor" />
				</Form.Item>
				<Form.Item name="model" label="Model">
					<Input placeholder="e.g. ZFB-v3" />
				</Form.Item>
				<Form.Item name="serialNumber" label="Serial Number">
					<Input placeholder="Optional" />
				</Form.Item>
				<Form.Item
					name="condition"
					label="Condition"
					initialValue="good">
				<Select options={[
					{ value: 'good', label: 'Good' },
					{ value: 'worn', label: 'Worn' },
					{ value: 'damaged', label: 'Damaged' }
				]} />
				</Form.Item>
				<Form.Item
					name="isControlled"
					label="Controlled (our boards/hardware)"
					initialValue={true}>
				<Select options={[
					{ value: true, label: 'Yes — our device' },
					{ value: false, label: 'No — 3rd party' }
				]} />
				</Form.Item>
				<Form.Item name="installedAt" label="Installed At">
					<DatePicker style={{ width: '100%' }} />
				</Form.Item>
				<Form.Item name="notes" label="Notes">
					<Input.TextArea rows={2} />
				</Form.Item>
			</Form>
		</Modal>
	)
}

// ─── Add Service modal ────────────────────────────────────────────────────────
interface AddServiceModalProps {
	equipmentId: number
	open: boolean
	onClose: () => void
	onCreated: () => void
}

const AddServiceModal: FC<AddServiceModalProps> = ({
	equipmentId,
	open,
	onClose,
	onCreated
}) => {
	const { t } = useTranslation()
	const [form] = Form.useForm()
	const [saving, setSaving] = useState(false)

	const handleOk = async () => {
		const values = await form.validateFields()
		setSaving(true)
		try {
			await createEquipmentService(equipmentId, {
				...values,
				servicedAt: values.servicedAt
					? values.servicedAt.toISOString()
					: new Date().toISOString()
			})
			onCreated()
			form.resetFields()
			onClose()
		} catch {
			message.error(t('Error'))
		} finally {
			setSaving(false)
		}
	}

	return (
		<Modal
			title="Add Service Record"
			open={open}
			onCancel={onClose}
			onOk={handleOk}
			confirmLoading={saving}
			okText="Add"
			destroyOnClose>
			<Form form={form} layout="vertical">
				<Form.Item
					name="serviceType"
					label="Service Type"
					rules={[{ required: true }]}>
					<Select
						placeholder="Select type"
						options={(
							['replacement', 'repair', 'calibration', 'inspection'] as ServiceType[]
						).map((v) => ({ value: v, label: SERVICE_TYPE_LABELS[v] }))}
					/>
				</Form.Item>
				<Form.Item
					name="servicedBy"
					label="Serviced By"
					rules={[{ required: true }]}>
					<Input placeholder="Technician name" />
				</Form.Item>
				<Form.Item name="servicedAt" label="Serviced At">
					<DatePicker
						showTime
						style={{ width: '100%' }}
					/>
				</Form.Item>
				<Form.Item name="notes" label="Notes">
					<Input.TextArea rows={2} />
				</Form.Item>
			</Form>
		</Modal>
	)
}

// ─── Equipment card ───────────────────────────────────────────────────────────
interface EquipmentCardProps {
	eq: Equipment
	onDeleted: (id: number) => void
	onServiceAdded: (id: number) => void
}

const EquipmentCard: FC<EquipmentCardProps> = ({
	eq,
	onDeleted,
	onServiceAdded
}) => {
	const { t } = useTranslation()
	const [addServiceOpen, setAddServiceOpen] = useState(false)

	const handleDelete = async () => {
		try {
			await deleteEquipment(eq.id)
			onDeleted(eq.id)
		} catch {
			message.error(t('Error'))
		}
	}

	return (
		<>
			<Flex
				vertical
				gap={8}
				style={{
					backgroundColor: '#fff',
					borderRadius: 12,
					padding: '14px 16px',
					boxShadow: '0 2px 8px rgba(26,48,79,0.08)'
				}}>
				{/* header row */}
				<Flex align="center" justify="space-between" gap={8}>
					<Flex align="center" gap={8} wrap="wrap">
						<Text strong style={{ fontSize: 15 }}>
							{eq.model || eq.componentType}
						</Text>
						<Tag color="blue">{eq.componentType}</Tag>
						<ConditionBadge condition={eq.condition} />
						{eq.isControlled && (
							<Tag color="purple">Our Device</Tag>
						)}
					</Flex>
					<Popconfirm
						title="Delete this equipment?"
						onConfirm={handleDelete}
						okText="Delete"
						okButtonProps={{ danger: true }}>
						<Button
							icon={<DeleteOutlined />}
							size="small"
							type="text"
							danger
						/>
					</Popconfirm>
				</Flex>

				{/* details */}
				<Flex gap={16} wrap="wrap">
					{eq.serialNumber && (
						<Text type="secondary" style={{ fontSize: 12 }}>
							SN: {eq.serialNumber}
						</Text>
					)}
					{eq.installedAt && (
						<Text type="secondary" style={{ fontSize: 12 }}>
							Installed:{' '}
							{new Date(eq.installedAt).toLocaleDateString(
								'ru'
							)}
						</Text>
					)}
				</Flex>

				{eq.notes && (
					<Text
						type="secondary"
						style={{ fontSize: 12, fontStyle: 'italic' }}>
						{eq.notes}
					</Text>
				)}

				{/* service history — controlled items only */}
				{eq.isControlled && (
					<>
						<Divider style={{ margin: '4px 0' }} />
						{eq.services.length > 0 ? (
							<Collapse
								ghost
								size="small"
								style={{ marginBottom: 4 }}
								items={[{
									key: 'services',
									label: (
										<Text
											type="secondary"
											style={{ fontSize: 12 }}>
											<ToolOutlined /> Service
											History (
											{eq.services.length})
										</Text>
									),
									children: (
										<Timeline
											style={{ marginTop: 8 }}
											items={eq.services.map((s) => ({
												children: (
													<Flex
														vertical
														style={{ fontSize: 12 }}>
														<Text strong>
															{
																SERVICE_TYPE_LABELS[
																	s.serviceType
																]
															}
														</Text>
														<Text type="secondary">
															{s.servicedBy} —{' '}
															{new Date(
																s.servicedAt
															).toLocaleDateString(
																'ru'
															)}
														</Text>
														{s.notes && (
															<Text
																type="secondary"
																style={{
																	fontStyle:
																		'italic'
																}}>
																{s.notes}
															</Text>
														)}
													</Flex>
												),
												key: s.id
											}))}
										/>
									)
								}]}
							/>
						) : (
							<Text
								type="secondary"
								style={{ fontSize: 12 }}>
								No service records
							</Text>
						)}
						<Button
							icon={<PlusOutlined />}
							size="small"
							type="dashed"
							onClick={() => setAddServiceOpen(true)}>
							Add Service Record
						</Button>
					</>
				)}
			</Flex>

			<AddServiceModal
				equipmentId={eq.id}
				open={addServiceOpen}
				onClose={() => setAddServiceOpen(false)}
				onCreated={() => onServiceAdded(eq.id)}
			/>
		</>
	)
}

// ─── Main component ───────────────────────────────────────────────────────────
export const CarrierOneEquipment: FC<{ carrierId: number }> = ({
	carrierId
}) => {
	const { t } = useTranslation()
	const [loading, setLoading] = useState(true)
	const [equipment, setEquipment] = useState<Equipment[]>([])
	const [addOpen, setAddOpen] = useState(false)

	const load = useCallback(async () => {
		setLoading(true)
		try {
			const data = await fetchEquipment(carrierId)
			setEquipment(data)
		} catch {
			message.error(t('Error'))
		} finally {
			setLoading(false)
		}
	}, [carrierId, t])

	useEffect(() => {
		load()
	}, [load])

	const handleCreated = (eq: Equipment) =>
		setEquipment((prev) => [...prev, eq])

	const handleDeleted = (id: number) =>
		setEquipment((prev) => prev.filter((e) => e.id !== id))

	// Re-fetch single item to get updated service list
	const handleServiceAdded = (equipmentId: number) => {
		fetchEquipmentById(equipmentId).then((updated) => {
			setEquipment((prev) =>
				prev.map((e) => (e.id === equipmentId ? updated : e))
			)
		})
	}

	const controlled = equipment.filter((e) => e.isControlled)
	const other = equipment.filter((e) => !e.isControlled)

	if (loading) {
		return (
			<Flex justify="center" style={{ paddingTop: 40 }}>
				<Spin />
			</Flex>
		)
	}

	return (
		<>
			<Flex
				vertical
				gap={16}
				style={{ width: '100%', maxWidth: 720 }}>
				<Flex justify="flex-end">
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() => setAddOpen(true)}>
						Add Equipment
					</Button>
				</Flex>

				{/* Our devices */}
				<Title level={5} style={{ margin: 0 }}>
					Our Devices (Controlled)
				</Title>
				{controlled.length === 0 ? (
					<Empty description="No controlled equipment" />
				) : (
					<Flex vertical gap={10}>
						{controlled.map((eq) => (
							<EquipmentCard
								key={eq.id}
								eq={eq}
								onDeleted={handleDeleted}
								onServiceAdded={handleServiceAdded}
							/>
						))}
					</Flex>
				)}

				{/* 3rd party */}
				{other.length > 0 && (
					<>
						<Title level={5} style={{ margin: 0 }}>
							Other Equipment
						</Title>
						<Flex vertical gap={10}>
							{other.map((eq) => (
								<EquipmentCard
									key={eq.id}
									eq={eq}
									onDeleted={handleDeleted}
									onServiceAdded={handleServiceAdded}
								/>
							))}
						</Flex>
					</>
				)}
			</Flex>

			<AddEquipmentModal
				carrierId={carrierId}
				open={addOpen}
				onClose={() => setAddOpen(false)}
				onCreated={handleCreated}
			/>
		</>
	)
}
